import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Save, AlertTriangle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import DriverBottomNav from "@/components/DriverBottomNav";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { fmt12 } from "@/lib/time";

// Admin-only "Quick book" — one-screen booking creation form. Used when the
// operator phones in an order, has a walk-up customer, or wants to seed test
// trips. Hits POST /api/admin/bookings/create-for-customer which bypasses the
// production-host gate and the service-area/overlap rejection so the admin
// can always create a booking — they just get warned about overlaps and can
// confirm with "force".
export default function AdminQuickBook() {
  const navigate = useNavigate();
  const { isAdmin, loading: authLoading } = useAuth();

  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [pickup, setPickup] = useState({ address: "", coords: null });
  const [dropoff, setDropoff] = useState({ address: "", coords: null });
  const [serviceType, setServiceType] = useState("rideshare");
  const [estimatedPrice, setEstimatedPrice] = useState("");
  const [distance, setDistance] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [time, setTime] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [warnings, setWarnings] = useState([]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAdmin) {
      toast.error("Admin login required");
      navigate("/driver/bookings");
    }
  }, [isAdmin, authLoading, navigate]);

  const TIME_OPTIONS = useMemo(() => {
    const arr = [];
    for (let h = 6; h <= 22; h++) {
      arr.push(`${String(h).padStart(2, "0")}:00`);
      arr.push(`${String(h).padStart(2, "0")}:30`);
    }
    return arr;
  }, []);

  const canSubmit =
    customerName.trim() &&
    customerPhone.trim() &&
    pickup.address &&
    dropoff.address &&
    estimatedPrice &&
    date &&
    time &&
    !submitting;

  const submit = async (force = false) => {
    if (!canSubmit) {
      toast.error("Fill all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        customer_name: customerName.trim(),
        customer_phone: customerPhone.trim(),
        customer_email: customerEmail.trim() || undefined,
        pickup_address: pickup.address,
        pickup_coords: pickup.coords || undefined,
        dropoff_address: dropoff.address,
        dropoff_coords: dropoff.coords || undefined,
        service_type: serviceType,
        estimated_price: parseFloat(estimatedPrice),
        distance_miles: parseFloat(distance || "0"),
        scheduled_date: date,
        scheduled_time: time,
        asap: false,
        payment_method: paymentMethod,
        notes: notes.trim() || undefined,
        force,
      };
      const { data } = await axios.post(`${API}/admin/bookings/create-for-customer`, payload);
      if (data?.needs_confirmation) {
        setWarnings(data.warnings || []);
        toast.warning("Conflicts found — review then confirm");
        return;
      }
      if (data?.attached_to_user) {
        toast.success(`Booking created for ${customerName} — attached to their account`);
      } else {
        toast.success(`Booking created for ${customerName} (no matching account found)`);
      }
      navigate("/driver/bookings");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create booking");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Toaster />
      <header className="sticky top-0 z-30 bg-[#050505]/95 backdrop-blur border-b border-white/5">
        <div className="px-5 py-4 flex items-center gap-3">
          <button onClick={() => navigate("/driver/bookings")} className="text-zinc-400 hover:text-white" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-black tracking-tight text-lg">Quick book</h1>
            <p className="text-xs text-zinc-500">Admin · phone-in or walk-up bookings</p>
          </div>
        </div>
      </header>

      <main className="px-5 py-5 space-y-4 pb-32">
        {warnings.length > 0 && (
          <Card className="bg-amber-500/10 border-amber-500/40" data-testid="warnings-card">
            <CardContent className="p-3 space-y-2">
              <div className="flex items-center gap-2 text-amber-300 font-bold text-sm">
                <AlertTriangle className="w-4 h-4" />
                Conflicts found
              </div>
              <ul className="text-xs text-amber-100/90 space-y-1 list-disc list-inside">
                {warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
              <Button
                onClick={() => submit(true)}
                className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold mt-2"
                data-testid="force-create-btn"
              >
                Create anyway
              </Button>
            </CardContent>
          </Card>
        )}

        <section className="space-y-2" data-testid="section-customer">
          <h2 className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Customer</h2>
          <input type="text" placeholder="Name *" value={customerName} onChange={(e) => setCustomerName(e.target.value)} className="w-full bg-[#121212] border border-white/10 rounded-lg px-3 py-2.5 text-sm" data-testid="input-name" />
          <input type="tel" placeholder="Phone * (e.g. 4844844844)" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} className="w-full bg-[#121212] border border-white/10 rounded-lg px-3 py-2.5 text-sm" data-testid="input-phone" />
          <input type="email" placeholder="Email (optional)" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} className="w-full bg-[#121212] border border-white/10 rounded-lg px-3 py-2.5 text-sm" data-testid="input-email" />
        </section>

        <section className="space-y-2" data-testid="section-trip">
          <h2 className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Trip</h2>
          <AddressAutocomplete
            value={pickup.address}
            onChange={(addr, coords) => setPickup({ address: addr, coords })}
            placeholder="Pickup address *"
            accent="emerald"
            testId="input-pickup"
          />
          <AddressAutocomplete
            value={dropoff.address}
            onChange={(addr, coords) => setDropoff({ address: addr, coords })}
            placeholder="Dropoff address *"
            accent="blue"
            testId="input-dropoff"
          />
          <div className="grid grid-cols-2 gap-2">
            <select value={serviceType} onChange={(e) => setServiceType(e.target.value)} className="bg-[#121212] border border-white/10 rounded-lg px-3 py-2.5 text-sm" data-testid="select-service">
              <option value="rideshare">Rideshare</option>
              <option value="delivery">Delivery</option>
              <option value="pickup">Pickup</option>
            </select>
            <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className="bg-[#121212] border border-white/10 rounded-lg px-3 py-2.5 text-sm" data-testid="select-payment">
              <option value="cash">Cash</option>
              <option value="stripe_tap">Stripe Tap-to-Pay</option>
              <option value="stripe">Stripe (pre-paid)</option>
              <option value="credits">Wallet credits</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input type="number" step="0.01" placeholder="Price ($) *" value={estimatedPrice} onChange={(e) => setEstimatedPrice(e.target.value)} className="bg-[#121212] border border-white/10 rounded-lg px-3 py-2.5 text-sm" data-testid="input-price" />
            <input type="number" step="0.1" placeholder="Miles" value={distance} onChange={(e) => setDistance(e.target.value)} className="bg-[#121212] border border-white/10 rounded-lg px-3 py-2.5 text-sm" data-testid="input-distance" />
          </div>
        </section>

        <section className="space-y-2" data-testid="section-when">
          <h2 className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Scheduled for</h2>
          <div className="grid grid-cols-2 gap-2">
            <input type="date" value={date} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setDate(e.target.value)} className="bg-[#121212] border border-white/10 rounded-lg px-3 py-2.5 text-sm" data-testid="input-date" />
            <select value={time} onChange={(e) => setTime(e.target.value)} className="bg-[#121212] border border-white/10 rounded-lg px-3 py-2.5 text-sm" data-testid="select-time">
              <option value="">Pick a time…</option>
              {TIME_OPTIONS.map((t) => <option key={t} value={t}>{fmt12(t)}</option>)}
            </select>
          </div>
        </section>

        <section data-testid="section-notes">
          <h2 className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-2">Notes</h2>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any special instructions" rows={2} className="w-full bg-[#121212] border border-white/10 rounded-lg px-3 py-2.5 text-sm" data-testid="input-notes" />
        </section>

        <Button
          onClick={() => submit(false)}
          disabled={!canSubmit}
          className="w-full h-12 bg-[#0047FF] hover:bg-[#003BE0] text-white font-bold rounded-xl shadow-[0_4px_24px_-8px_rgba(0,71,255,0.6)]"
          data-testid="submit-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {submitting ? "Creating…" : "Create booking"}
        </Button>
      </main>
      <DriverBottomNav />
    </div>
  );
}
